import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { RoutineType, RoutineTypeDB } from '../../../../domain/db/RoutineTypeDB'
import { RoutineLevelDB } from '../../../../domain/db/RoutineLevelDB'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { routineSchema } from '../validators'
import { useTranslation } from 'react-i18next'
import { LabelProps } from '../../../../components/Label'
import { Container, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import api from '../../../../services/api'
import { InputTags } from '../../../../components/InputTags'
import { TextArea } from '../../../../components/TextArea'
import { LoadingButton } from '@mui/lab'
import { FormInputText } from 'components/FormInputText'
import { FormInputSelect } from 'components/FormInputSelect'
import { useAppDispatch } from 'redux/hooks'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { Button } from 'components/Button'

type RoutineFormProps = {
  id: string
  name: string
  type: string
  level: string
  note?: string
  labels?: LabelProps[]
}

export const RoutineForm = ({ isVisible, routine, onClose, onSubmitted, loading }: any) => {
  const { control, handleSubmit, reset, setValue, formState: { errors } }: any = useForm<RoutineFormProps>({
    resolver: yupResolver(routineSchema)
  })
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  useEffect(() => {
    if (isVisible) {
      reset()
    }
    dataFetch().catch(console.error)
  }, [isVisible, routine])

  const dataFetch = async () => {
    if (routine == null) return
    const fields = ['id', 'name', 'type', 'level', 'note', 'labels']
    fields.forEach((field: any) => setValue(field, routine[field]))
  }

  const handleUpsertRoutine = async (routine: RoutineFormProps): Promise<void> => {
    try {
      let saveMessage = t('routine.create_success')
      if (routine.id !== undefined) {
        saveMessage = t('routine.update_success')
        await api.put(`routines/${routine.id}`, routine)
      } else {
        await api.post('routines', routine)
      }
      onSubmitted()
      dispatch(showMessage({ message: saveMessage }))
    } catch (e: any) {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={isVisible}
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
    >
      <DialogTitle>{(routine ? t('routine.editRoutine') : t('routine.newRoutine'))}</DialogTitle>
      <DialogContent>
        <Container component="main">
          <Stack sx={{ mt: 3 }}>
            <Controller
              name="name"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputText
                  autoFocus
                  name="name"
                  value={value}
                  placeholder={`${t('routine.field.name.placeholder')}`}
                  label={`${t('routine.field.name.label')}`}
                  onChange={onChange}
                  control={control}
                  errorMessage={errors.name?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="type"
              defaultValue={RoutineType.EMPTY}
              render={({ field: { onChange, value } }) => (
                <FormInputSelect
                  name="type"
                  required={true}
                  control={control}
                  value={value}
                  onChange={onChange}
                  placeholder={`${t('routine.field.type.placeholder')}`}
                  label={`${t('routine.field.type.label')}`}
                  items={RoutineTypeDB}
                  errorMessage={errors.type?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="level"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <FormInputSelect
                  name="level"
                  required={true}
                  control={control}
                  value={value}
                  onChange={onChange}
                  placeholder={`${t('routine.field.level.placeholder')}`}
                  label={`${t('routine.field.level.label')}`}
                  items={RoutineLevelDB}
                  errorMessage={errors.level?.message}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              name="note"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <TextArea
                  value={value}
                  label={`${t('routine.field.note.label')}`}
                  placeholder={`${t('routine.field.note.placeholder')}`}
                  autoCapitalize="none"
                  onChange={onChange}
                />
              )} />
          </Stack>
          <Stack sx={{ mt: 3 }}>
            <Controller
              control={control}
              defaultValue={[]}
              name="labels"
              render={({ field: { onChange, value } }) => {
                return (
                  <InputTags
                    label={`${t('routine.field.tags.label')}`}
                    value={value}
                    onChange={onChange}
                  />
                )
              }} />
          </Stack>
        </Container>
      </DialogContent>
      <DialogActions>
        <LoadingButton loading={loading} onClick={handleSubmit(handleUpsertRoutine)} variant="contained">
          {t('general.btn.save')}
        </LoadingButton>
        <Button title={t('general.btn.dismiss')} variant="contained" color="secondary" onClick={onClose} />
      </DialogActions>
    </Dialog>
  )
}
